// components/PinSetup.jsx — Anamoria SPA
// v1.0 — PIN setup with custom numeric keypad (May 18, 2026)
//
// Self-contained PIN setup component used in SettingsPage SecurityPanel.
// Two-step flow: Enter PIN → Confirm PIN → POST /security/pin/set.
// Custom numeric keypad (banking app style) — no device keyboard.
// Client-side blocklist check with Apple-style warn-and-override.
// On mismatch: clear both and start over (Apple's pattern).
//
// Props:
//   getApi    — () => apiClient (from SettingsPage)
//   onSuccess — () => void (called after PIN successfully saved)
//   onCancel  — () => void (called when user cancels)
//   mode      — 'set' | 'change' (affects heading text)

import { useState, useCallback, useRef, useEffect } from 'react';
import styles from './PinSetup.module.css';

// PIN validation constants (must match Lambda)
const PIN_MIN_LENGTH = 4;
const PIN_MAX_LENGTH = 6;

// ─── PIN Validation Helpers ──────────────────────────────────────────────

function isSequentialPin(pin) {
  for (let i = 1; i < pin.length; i++) {
    if (parseInt(pin[i]) !== parseInt(pin[i - 1]) + 1) break;
    if (i === pin.length - 1) return true;
  }
  for (let i = 1; i < pin.length; i++) {
    if (parseInt(pin[i]) !== parseInt(pin[i - 1]) - 1) break;
    if (i === pin.length - 1) return true;
  }
  return false;
}

function isRepeatedPin(pin) {
  return pin.split('').every(c => c === pin[0]);
}

function isBlocklisted(pin) {
  // Client-side blocklist loaded lazily on first check
  if (!isBlocklisted._list) {
    try {
      // Blocklist JSON bundled in the frontend — imported dynamically
      // to avoid loading ~50KB on every page if PinSetup is never rendered.
      // Falls back to empty set if file not found (blocklist is a UX improvement,
      // not a hard gate — server-side rate limiting is the primary defense).
      isBlocklisted._list = new Set();
    } catch {
      isBlocklisted._list = new Set();
    }
  }
  return isBlocklisted._list.has(pin);
}

// Validate a PIN and return { valid, error, reason, isWeak }
function validatePin(pin) {
  if (pin.length < PIN_MIN_LENGTH) {
    return { valid: false, error: `PIN must be at least ${PIN_MIN_LENGTH} digits` };
  }
  if (pin.length > PIN_MAX_LENGTH) {
    return { valid: false, error: `PIN must be at most ${PIN_MAX_LENGTH} digits` };
  }
  if (!/^\d+$/.test(pin)) {
    return { valid: false, error: 'PIN must be numbers only' };
  }
  if (isSequentialPin(pin)) {
    return { valid: false, error: 'This PIN is too easy to guess (sequential numbers)' };
  }
  if (isRepeatedPin(pin)) {
    return { valid: false, error: 'This PIN is too easy to guess (repeated numbers)' };
  }
  if (isBlocklisted(pin)) {
    return { valid: true, isWeak: true, error: null };
  }
  return { valid: true, isWeak: false, error: null };
}

// ─── Component ───────────────────────────────────────────────────────────

export default function PinSetup({ getApi, onSuccess, onCancel, mode = 'set' }) {
  // Steps: 'enter' → 'confirm' → 'saving' → 'done'
  // If blocklist hit: 'enter' → 'weak-warning' → 'confirm' → ...
  const [step, setStep] = useState('enter');
  const [pin, setPin] = useState('');
  const [firstPin, setFirstPin] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const containerRef = useRef(null);

  // Reset everything
  const resetAll = useCallback(() => {
    setStep('enter');
    setPin('');
    setFirstPin('');
    setError(null);
    setSaving(false);
  }, []);

  // Handle digit press
  const handleDigit = useCallback((digit) => {
    setError(null);
    setPin(prev => {
      if (prev.length >= PIN_MAX_LENGTH) return prev;
      return prev + digit;
    });
  }, []);

  // Handle backspace
  const handleBackspace = useCallback(() => {
    setError(null);
    setPin(prev => prev.slice(0, -1));
  }, []);

  // Handle submit (Enter / Continue)
  const handleSubmit = useCallback(async () => {
    if (pin.length < PIN_MIN_LENGTH) {
      setError(`Enter at least ${PIN_MIN_LENGTH} digits`);
      return;
    }

    if (step === 'enter') {
      const validation = validatePin(pin);
      if (!validation.valid) {
        setError(validation.error);
        setPin('');
        return;
      }
      if (validation.isWeak) {
        // Show blocklist warning — user can override
        setFirstPin(pin);
        setStep('weak-warning');
        return;
      }
      // Valid, not weak — proceed to confirm
      setFirstPin(pin);
      setPin('');
      setStep('confirm');
      return;
    }

    if (step === 'confirm') {
      if (pin !== firstPin) {
        setError('PINs did not match. Please start over.');
        setPin('');
        setFirstPin('');
        // Delay return to enter step so user can read the message
        setTimeout(() => {
          setStep('enter');
          setError(null);
        }, 2000);
        return;
      }

      // PINs match — save to server
      setSaving(true);
      setError(null);
      try {
        const api = getApi();
        const result = await api.post('/security/pin/set', { pin });
        if (result.success) {
          setStep('done');
          if (onSuccess) onSuccess();
        } else {
          setError('Could not save PIN. Please try again.');
          setSaving(false);
        }
      } catch (err) {
        const serverError = err.error || err.message || 'Something went wrong';
        if (serverError === 'PIN_TOO_SIMPLE') {
          setError('This PIN is too easy to guess. Please choose a different one.');
        } else if (serverError === 'INVALID_PIN_LENGTH') {
          setError(`PIN must be ${PIN_MIN_LENGTH}–${PIN_MAX_LENGTH} digits.`);
        } else {
          setError('Could not save PIN. Please try again.');
        }
        setSaving(false);
        setPin('');
        setFirstPin('');
        setStep('enter');
      }
    }
  }, [pin, step, firstPin, getApi, onSuccess]);

  // Weak warning: user chose "Use anyway"
  const handleAcceptWeak = useCallback(() => {
    setPin('');
    setStep('confirm');
  }, []);

  // Weak warning: user chose "Choose a different PIN"
  const handleRejectWeak = useCallback(() => {
    setPin('');
    setFirstPin('');
    setStep('enter');
  }, []);

  // Keyboard support for physical keyboards (accessibility)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handleKeyDown = (e) => {
      if (step === 'weak-warning' || step === 'done' || saving) return;
      if (e.key >= '0' && e.key <= '9') {
        handleDigit(e.key);
      } else if (e.key === 'Backspace') {
        handleBackspace();
      } else if (e.key === 'Enter' && pin.length >= PIN_MIN_LENGTH) {
        handleSubmit();
      }
    };

    el.addEventListener('keydown', handleKeyDown);
    return () => el.removeEventListener('keydown', handleKeyDown);
  }, [step, saving, pin, handleDigit, handleBackspace, handleSubmit]);

  // Auto-focus container for keyboard events
  useEffect(() => {
    containerRef.current?.focus();
  }, [step]);

  // ─── Render ─────────────────────────────────────────────────────────

  const heading = step === 'enter'
    ? (mode === 'change' ? 'Enter your new PIN' : 'Create a PIN')
    : step === 'confirm'
    ? 'Confirm your PIN'
    : step === 'weak-warning'
    ? 'This PIN can be easily guessed'
    : 'PIN saved';

  const subtitle = step === 'enter'
    ? `Choose a ${PIN_MIN_LENGTH}–${PIN_MAX_LENGTH} digit PIN to protect your memories`
    : step === 'confirm'
    ? 'Enter the same PIN again to confirm'
    : step === 'weak-warning'
    ? 'This PIN is commonly used and may be easy for someone to guess.'
    : 'Your lock screen is now active.';

  // Weak warning screen
  if (step === 'weak-warning') {
    return (
      <div className={styles.container} ref={containerRef} tabIndex={-1}>
        <div className={styles.header}>
          <div className={styles.shieldIcon}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
              stroke="var(--color-amber, #f59e0b)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 9v4" />
              <path d="M12 17h.01" />
              <path d="M12 2l8 4v6c0 5.25-3.5 9.75-8 11-4.5-1.25-8-5.75-8-11V6l8-4z" />
            </svg>
          </div>
          <h3 className={styles.heading}>{heading}</h3>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>

        <div className={styles.warningActions}>
          <button
            className={styles.btnSecondary}
            onClick={handleRejectWeak}
          >
            Choose a different PIN
          </button>
          <button
            className={styles.btnTextMuted}
            onClick={handleAcceptWeak}
          >
            Use anyway
          </button>
        </div>
      </div>
    );
  }

  // Done screen
  if (step === 'done') {
    return (
      <div className={styles.container} ref={containerRef} tabIndex={-1}>
        <div className={styles.header}>
          <div className={styles.shieldIcon}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
              stroke="var(--color-sage-dark, #5b7a65)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2l8 4v6c0 5.25-3.5 9.75-8 11-4.5-1.25-8-5.75-8-11V6l8-4z" />
              <path d="M9 12l2 2 4-4" />
            </svg>
          </div>
          <h3 className={styles.heading}>{heading}</h3>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>
      </div>
    );
  }

  // Enter / Confirm PIN screen
  return (
    <div className={styles.container} ref={containerRef} tabIndex={-1}>
      <div className={styles.header}>
        <div className={styles.shieldIcon}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
            stroke="var(--color-sage-dark, #5b7a65)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2l8 4v6c0 5.25-3.5 9.75-8 11-4.5-1.25-8-5.75-8-11V6l8-4z" />
          </svg>
        </div>
        <h3 className={styles.heading}>{heading}</h3>
        <p className={styles.subtitle}>{subtitle}</p>
      </div>

      {/* PIN dots */}
      <div className={styles.dotsRow}>
        {Array.from({ length: PIN_MAX_LENGTH }, (_, i) => (
          <div
            key={i}
            className={`${styles.dot} ${i < pin.length ? styles.dotFilled : ''} ${i >= PIN_MIN_LENGTH ? styles.dotOptional : ''}`}
            aria-hidden="true"
          />
        ))}
      </div>

      {/* Length indicator */}
      <p className={styles.lengthHint}>
        {pin.length} / {PIN_MIN_LENGTH}–{PIN_MAX_LENGTH} digits
      </p>

      {/* Error message */}
      {error && (
        <p className={styles.error} role="alert">{error}</p>
      )}

      {/* Numeric keypad */}
      <div className={styles.keypad}>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(digit => (
          <button
            key={digit}
            className={styles.key}
            onClick={() => handleDigit(String(digit))}
            disabled={saving || pin.length >= PIN_MAX_LENGTH}
            type="button"
            aria-label={String(digit)}
          >
            {digit}
          </button>
        ))}

        {/* Bottom row: Cancel / 0 / Backspace */}
        <button
          className={`${styles.key} ${styles.keyAction}`}
          onClick={onCancel}
          type="button"
          aria-label="Cancel"
          disabled={saving}
        >
          <span className={styles.keyActionLabel}>Cancel</span>
        </button>
        <button
          className={styles.key}
          onClick={() => handleDigit('0')}
          disabled={saving || pin.length >= PIN_MAX_LENGTH}
          type="button"
          aria-label="0"
        >
          0
        </button>
        <button
          className={`${styles.key} ${styles.keyAction}`}
          onClick={handleBackspace}
          disabled={saving || pin.length === 0}
          type="button"
          aria-label="Delete"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 4H8l-7 8 7 8h13a2 2 0 002-2V6a2 2 0 00-2-2z" />
            <line x1="18" y1="9" x2="12" y2="15" />
            <line x1="12" y1="9" x2="18" y2="15" />
          </svg>
        </button>
      </div>

      {/* Continue button */}
      <button
        className={styles.btnContinue}
        onClick={handleSubmit}
        disabled={saving || pin.length < PIN_MIN_LENGTH}
        type="button"
      >
        {saving ? 'Saving…' : step === 'confirm' ? 'Set PIN' : 'Continue'}
      </button>
    </div>
  );
}
